import {
  PaginationState, createColumnHelper, flexRender,
  getCoreRowModel, getExpandedRowModel, getSortedRowModel,
  SortingState, useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import {  useLocation } from "react-router-dom";
import React, { useState, useEffect, useContext } from "react";
import { PaymentRowObj } from "..";
import InfoModal from "./InfoModal";
import AdvancedInfoModal from "./AdvancedInfoModal";
import RefundModal from "./RefundModal";
import { convertToFloat } from "utils/formatNumber";
import { BsArrowDown, BsArrowUp, BsReceipt } from "react-icons/bs";
import { ClientContext } from "ClientProvider";
import { DownloadClientCSV } from "api/payments";
import { toast } from "react-hot-toast";
import FilterModal from "./FilterModal";
import { FaDownload } from "react-icons/fa";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import Searchbox from "components/fields/Searchbox";
import { getImage } from "utils/commonFunction";
import Pagination from "components/pagination";
import StatusRender from "./StatusRender";
import RetryModal from "./RetryModal";
import ChangeStatusModal from "./StatusChangeModal";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";
import CustomToolTip from "components/tooltips/new";
import { RiArrowUpDownFill } from "react-icons/ri";

import visa from "assets/svg/card_type/visa.svg";
import MasterCard from "assets/svg/card_type/mastercard.svg";
import Discover from "assets/svg/card_type/discover.svg";
import Amex from "assets/svg/card_type/amex.svg";
import MaestroCard from "assets/svg/card_type/maestro.svg";
import DinersClub from "assets/svg/card_type/dinersclub.svg";
import JCB from "assets/svg/card_type/jcb.svg";
import UnionPay from "assets/svg/card_type/unionpay.svg";
import TripleToggleSwitch from "./TripleToggleSwitch";
import { AiFillMinusCircle, AiFillPlusCircle } from "react-icons/ai";

const columnHelper = createColumnHelper<PaymentRowObj>();

const CARD_SVGS: Record<string, string> = {
  VISA: visa, MasterCard, Discover, Amex, MaestroCard, DinersClub, JCB, UnionPay,
};

const getCardSVG = (type: string) => {
  const src = CARD_SVGS[type];
  if (!src) return <span className="txn-muted">—</span>;
  return <img style={{ maxWidth: "56px", height: "auto" }} src={src} alt={type} title={type} />;
};

function PaymentTable(props: {
  tableData: any; fetchPayments: () => void; filterData: () => void;
  isLoading: boolean; page: number; setPage: any;
  totalpage: number; totalItems: number; currentPage: number;
  clients: any[]; allGateways: any[]; allCardType: any[];
  curretClient: string; setCurrentClient: any;
  pageSize: number; setPageSize: any; roleData: any;
  filter: any; setFilter: (e: any) => void; setClearClick: () => void;
  onValueChange: (value: string) => void;
  setLocalFilters?: (e: any) => void; localFilters?: any; isAllDataReady?: any;searchTexts?:any;client_id?:any;
}) {
  const {
    tableData, filterData, fetchPayments, page, clients, allGateways, allCardType,
    setPage, currentPage, totalpage, curretClient, setCurrentClient, totalItems,
    pageSize, setPageSize, roleData, filter, setFilter, setClearClick,
    onValueChange, setLocalFilters, localFilters, isAllDataReady,searchTexts,client_id
  } = props;

  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [DownloadLoading, setDownloadLoading] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const { paymentType , loadingTransaction } = useContext(ClientContext);

  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const location = useLocation();

  React.useEffect(() => { setData(tableData ?? []); }, [tableData]);

  const method = roleData[0]?.payment?.value?.payment_show_method_name;

  /* ── Error extractor for declined statuses ── */
  const extractError = (gatewayResponse: any): string => {
    const errorKeys = [
      "errorMessage",
      "message",
      "decline_reason",
      "error_message",
      "failure_message",
      "failure_code",
      "unified_message",
      "ResponseDescription",
      "description"
    ];

    let lastValue = "";

    const search = (data: any) => {
      if (!data || typeof data !== "object") return;

      for (const key of Object.keys(data)) {
        const value = data[key];

        // Check known error keys
        if (
          errorKeys.includes(key) &&
          key !== "_content" &&
          typeof value === "string" &&
          value.trim()
        ) {
          lastValue = value;
        }

        // Recursively search nested objects/arrays
        if (value && typeof value === "object") {
          search(value);
        }

        // Handle JSON stored inside _content
        if (key === "_content" && typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            search(parsed);
          } catch {
            // Ignore invalid JSON
          }
        }
      }
    };

    search(gatewayResponse);

    return lastValue;
  };

  /* ── Status tooltip content ── */
  const StatusHistoryContent = ({ info }: { info: any }) => {
    const history = info?.row?.original?.payment_history;
    if (!history?.length) return <span className="txn-no-history">No history available.</span>;
    const isDeclined = ["DECLINED","ERRORED","FAILED"].includes(info.getValue() as string);
    const errorMessage = isDeclined ? extractError(info?.row?.original?.gateway_response?.gateway_response) : "";
    return (
      <>
        {history.map((h: any, i: number) => (
          <div key={i} className="txn-history-item">
            {i === 0 ? (
              <p className="txn-history-txt">
                Created at <strong>{h?.created_at.replace("GMT","")}</strong> by API call from [{h?.client} — {h?.ip}] with status <strong>{h?.status}</strong>
                {isDeclined && <><br/>Reason: {errorMessage || "Not mentioned by gateway"}</>}
              </p>
            ) : h?.status === "CHARGEBACK" ? (
              <div className="txn-history-txt">
                <p>Status: {h?.previous_status} → <strong>{h?.status}</strong> at {h?.status_changed_date.replace("GMT","")}</p>
                <p>ARN: {h?.arn || "—"}</p>
                <p>Reason: {h?.reason || "—"}</p>
              </div>
            ) : (
              <p className="txn-history-txt">
                Status: {h?.previous_status} → <strong>{h?.status}</strong> at {h?.status_changed_date.replace("GMT","")}
                {isDeclined && <><br/>Reason: {errorMessage || "Not mentioned by gateway"}</>}
              </p>
            )}
          </div>
        ))}
      </>
    );
  };

  /*
   * Main table intentionally contains only the fields that are useful for
   * scanning a transaction list. The remaining transaction information is
   * rendered in the expandable details row below each transaction.
   */
  const columns = [
    // columnHelper.display({
    //   id: "expand",
    //   header: () => <span className="txn-th" aria-label="Expand"></span>,
    //   cell: ({ row }) => (
    //     <button
    //       type="button"
    //       className={`txn-expand-btn${row.getIsExpanded() ? " is-expanded" : ""}`}
    //       onClick={() => row.toggleExpanded()}
    //       aria-label={row.getIsExpanded() ? "Collapse transaction details" : "Expand transaction details"}
    //       aria-expanded={row.getIsExpanded()}
    //     >
    //       {row.getIsExpanded() ? "−" : "+"}
    //     </button>
    //   ),
    //   enableSorting: false,
    // }),

    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <span className="txn-th">Date</span>,
      cell: (info: any) => (
        <div
          className="clt-expand-cell"
          onClick={() => {
            const id = String(info.row.original?.id);
            setExpandedRowId((prev) => (prev === id ? null : id));
          }}
        >
          {expandedRowId === String(info.row.original?.id)
            ? <AiFillMinusCircle className="clt-expand-icon clt-expand-icon--open" />
            : <AiFillPlusCircle className="clt-expand-icon" />}
          <span>{info.getValue()}</span>
        </div>
      ),
      // cell: (info) => <span className="txn-date">{info.getValue()}</span>,
      sortingFn: (a, b) =>
        new Date(a.original.created_at).getTime() - new Date(b.original.created_at).getTime(),
      enableSorting: true,
    }),

    columnHelper.accessor("client", {
      id: "client",
      header: () => <span className="txn-th">Client</span>,
      cell: (info: any) => (
        <div className="txn-client-cell">
          <div className="txn-client-avatar">{info.getValue()?.charAt(0)?.toUpperCase() || "C"}</div>
          <span className="txn-client-name">{info.getValue() || "—"}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.client || "";
        const b = rowB.original.client || "";
        const result = a.localeCompare(b);
        if (result !== 0) return result;
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),

    

    columnHelper.accessor("amount", {
      id: "amount",
      header: () => <span className="txn-th">Amount</span>,
      cell: (info: any) => (
        <div className="txn-amount-cell">
          <span className="txn-amount">
            {convertToFloat(info.getValue())} {info?.row?.original?.currency}
          </span>
          {info?.row?.original?.converted_amount && info?.row?.original?.converted_currency && (
            <span className="txn-amount-conv">
              ({Number(info?.row?.original?.converted_amount).toFixed(2)}{" "}
              {info?.row?.original?.converted_currency})
            </span>
          )}
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.amount || 0);
        const b = Number(rowB.original.amount || 0);
        if (a !== b) return a - b;
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),

    columnHelper.accessor("et_id", {
      id: "et_id",
      header: () => <span className="txn-th">Transaction ID</span>,
      cell: (info) => <ShortTruncateCopy info={info?.getValue()} showCopy={true} />,
    }),

    columnHelper.accessor("id", {
      id: "id",
      header: () => <span className="txn-th">Internal ID</span>,
      cell: (info: any) => <ShortTruncateCopy info={info?.getValue()} showCopy={true} />,
    }),

    columnHelper.accessor("reference_id", {
      id: "reference_id",
      header: () => <span className="txn-th">Reference ID</span>,
      cell: (info) => {
        const gName = info?.getValue();
        return gName ? (
          <ShortTruncateCopy info={info?.getValue()} showCopy={true} />
        ) : (
          "-"
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.reference_id || "";
        const b = rowB.original.reference_id || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),

    columnHelper.accessor("gateway_id", {
      id: "gateway_id",
      header: () => <span className="txn-th">Gateway Name</span>,
      cell: (info) => {
        const gName = info?.cell?.row?.original?.gateway_name;
        return gName ? (
          <div className="txn-gateway-cell">
            <img
              style={{ height: "auto", width: "16px" }}
              src={getImage(gName)}
              alt={gName}
            />
            <span className="txn-gateway-name">{gName}</span>
          </div>
        ) : (
          <ShortTruncateCopy
            info={info?.cell?.row?.original?.gateway_id || "—"}
            showCopy={true}
          />
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.gateway_id || "";
        const b = rowB.original.gateway_id || "";
        const result = a.localeCompare(b);
        if (result !== 0) return result;
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),

    columnHelper.accessor("status", {
      id: "status",
      header: () => <span className="txn-th">Status</span>,
      cell: (info) => (
        <CustomToolTip
          content={<StatusHistoryContent info={info} />}
          trigger={
            <button className="txn-status-trigger">
              <StatusRender
                status={info?.row?.original?.status}
                value={info.getValue()}
              />
            </button>
          }
          header="Status History"
        />
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.status || "";
        const b = rowB.original.status || "";
        const result = a.localeCompare(b);
        if (result !== 0) return result;
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),

  ];

  const table = useReactTable({
    data,
    columns: columns,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  const handelOnDownloadCSV = () => {
    setDownloadLoading(true);
    DownloadClientCSV(filter?.status,client_id, filter?.client, filter?.gateway, filter?.payment_status, filter?.dates?.[0], filter?.dates?.[1],searchTexts)
      .then((csvText) => {
        const blob = new Blob([csvText], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        // link.download = "transactions.csv";
        const now = new Date();
        const timestamp =
          `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_` +
          `${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;
        link.download = `transactions_${timestamp}.csv`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        toast.success("Download complete");
        setTimeout(() => setDownloadLoading(false), 2000);
      })
      .catch((err) => { toast.error(err?.response?.data?.message ?? "Download failed"); setTimeout(() => setDownloadLoading(false), 2000); });
  };

  const labels = {
    left: { title: "All", value: "left" },
    right: { title: "Live", value: "right" },
    center: { title: "Test", value: "center" },
  };

  const onChange = (value: any) => {
    if (value === "left") setFilter({ ...filter, status: "all" });
    else if (value === "right") setFilter({ ...filter, status: "live" });
    else if (value === "center") setFilter({ ...filter, status: "test" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleValueChange = (e: any) => { setExpandedRowId(null);  onValueChange(e); };

  const resetRow = () => {
    setExpandedRowId(null);
  }

  React.useEffect(() => { setExpandedRowId(null);  }, [page, pageSize]);

  return (
    <div className="txn-card">
      {/* ── Header ── */}
      <div className="txn-header">
        <div className="txn-header-left">
          <div className="txn-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
          </div>
          <div>
            <h3 className="txn-title">All Transactions</h3>
            <p className="txn-subtitle">{totalItems > 0 ? `${totalItems.toLocaleString()} total transaction${totalItems !== 1 ? "s" : ""}` : "No transactions"}</p>
          </div>
        </div>
        <div className="txn-header-right">
          <div className="nb-toggle-wrap">
            <TripleToggleSwitch
              labels={labels}
              onChange={onChange}
              key={location.pathname}
              loadingTransaction={loadingTransaction}
            />
          </div>
          <button
            className="txn-download-btn"
            onClick={handelOnDownloadCSV}
            disabled={DownloadLoading || table.getRowModel().rows.length === 0}
          >
            {DownloadLoading ? <span className="txn-btn-spinner" /> : <FaDownload size={12} />}
            <span>Export CSV</span>
          </button>
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="txn-table-wrap">
        {props.isLoading ? (
          <div className="txn-loading">
            <div className="txn-spinner" />
            <p className="txn-loading-txt">Loading transactions…</p>
          </div>
        ) : (
          <table className="txn-table">
            <thead>
              {table.getHeaderGroups().map((hg, i) => (
                <tr key={i}>
                  {hg.headers.map((h, ci) => (
                    <th key={ci} colSpan={h.colSpan}
                      onClick={h.column.getToggleSortingHandler()}
                      className={`txn-thead-th${h.column.getCanSort() ? " txn-thead-th--sort" : ""}`}
                    >
                      <div className="txn-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="txn-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> :
                             h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> :
                             <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`txn-tbody-tr${expandedRowId === String(row.original?.id) ? " txn-tbody-tr--expanded" : ""}`}
                    >
                      {row.getVisibleCells().map((cell, ci) => (
                        <td key={ci} className="txn-tbody-td">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>

                    {expandedRowId === String(row.original?.id) && (
                      <tr className="txn-details-tr" >
                        <td
                          colSpan={row.getVisibleCells().length}
                          className="txn-details-td"
                        >
                          <div className="txn-details-panel">
                            <div className="txn-details-header">
                              <div>
                                <div className="txn-details-title">
                                  Transaction Details
                                </div>
                                <div className="txn-details-subtitle">
                                  Complete information for this transaction
                                </div>
                              </div>
                              <div className="txn-details-date">
                                <span>Date & Time</span>
                                <strong>
                                  {row.original?.created_at || "—"}
                                </strong>
                              </div>
                            </div>
                            <div className="txn-details-grid">
                              <div className="txn-detail-item">
                                <span className="txn-detail-label">Transaction ID</span>
                                <div className="txn-detail-value txn-detail-copy">
                                  <ShortTruncateCopy
                                    info={row.original?.et_id}
                                    showCopy={true}
                                  />
                                </div>
                              </div>
                              <div className="txn-detail-item">
                                <span className="txn-detail-label">Internal ID</span>
                                <div className="txn-detail-value txn-detail-copy">
                                  <ShortTruncateCopy
                                    info={row.original?.id}
                                    showCopy={true}
                                  />
                                </div>
                              </div>
                              <div className="txn-detail-item">
                                <span className="txn-detail-label">Customer ID</span>
                                <div className="txn-detail-value txn-detail-copy">
                                  {row.original?.customer_name ? (
                                    <ShortTruncateCopy
                                      info={row.original.customer_name}
                                      showCopy={true}
                                    />
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </div>
                              <div className="txn-detail-item">
                                <span className="txn-detail-label">Reference ID</span>
                                <div className="txn-detail-value txn-detail-copy">
                                  {row.original?.reference_id ? (
                                    <ShortTruncateCopy
                                      info={row.original.reference_id}
                                      showCopy={true}
                                    />
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </div>
                              <div className="txn-detail-item">
                                <span className="txn-detail-label">Descriptor</span>
                                <span className="txn-detail-value">
                                  {row.original?.meta_info?.Descriptor || "—"}
                                </span>
                              </div>
                              <div className="txn-detail-item">
                                <span className="txn-detail-label">Card Type</span>
                                <span className="txn-detail-value txn-card-detail">
                                  {row.original?.card_type ? (
                                    <>
                                      {getCardSVG(row.original.card_type)}
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </span>
                              </div>
                              <div className="txn-detail-item">
                                <span className="txn-detail-label">Currency</span>
                                <span className="txn-detail-value">
                                  {row.original?.currency || "—"}
                                </span>
                              </div>
                              <div className="txn-detail-item">
                                <span className="txn-detail-label">Converted Amount</span>
                                <span className="txn-detail-value">
                                  {(row.original as any)?.converted_amount != null
                                    ? `${Number((row.original as any).converted_amount).toFixed(2)} ${(row.original as any)?.converted_currency || ""}`
                                    : "—"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr><td colSpan={12}>
                  <div className="txn-empty">
                    <div className="txn-empty-icon">
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
                      </svg>
                    </div>
                    <p className="txn-empty-title">No transactions found</p>
                    <p className="txn-empty-sub">Try adjusting your filters or date range</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      <div className="txn-pagination-wrap">
        <Pagination setPage={setPage} page={page} totalpage={totalpage} currentPage={currentPage} pageSize={pageSize} setPageSize={setPageSize} arraySize={[50, 100, 200]} />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .nb-toggle-wrap { display: flex; align-items: center; }

        .txn-card { font-family:'Plus Jakarta Sans',sans-serif; background:#fff;  overflow:hidden; box-shadow:0 2px 8px rgba(15,23,42,0.06); }

        .clt-expand-cell { display: flex; align-items: center; gap: 7px; cursor: pointer; font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; }
        .clt-expand-cell--sm { gap: 6px; font-size: 12.5px; }
        .clt-expand-icon { width: 18px; height: 18px; color: #94a3b8; flex-shrink: 0; transition: color .15s; }
        .clt-expand-icon--open { color: #16a34a; }
        .clt-expand-cell:hover .clt-expand-icon { color: #16a34a; }
        
        .txn-header { padding:20px 24px 16px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; }
        .txn-header-left { display:flex; align-items:center; gap:12px; }
        .txn-header-icon { width:40px; height:40px; border-radius:11px; background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; display:flex; align-items:center; justify-content:center; color:#2563eb; flex-shrink:0; }
        .txn-title { font-size:15px; font-weight:700; color:#0f172a; letter-spacing:-0.2px; margin-bottom:2px; line-height:22px; }
        .txn-subtitle { font-size:12px; color:#94a3b8; font-weight:500; }
        .txn-header-right { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }

        .txn-download-btn { display:inline-flex; align-items:center; gap:6px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:700; color:#fff; padding:9px 16px; border-radius:10px; border:none; background:linear-gradient(135deg,#1d4ed8,#2563eb); cursor:pointer; box-shadow:0 3px 10px rgba(37,99,235,.24); transition:all .15s; white-space:nowrap; }
        .txn-download-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 18px rgba(37,99,235,.32); }
        .txn-download-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }
        .txn-btn-spinner { width:13px; height:13px; border:2px solid rgba(255,255,255,.35); border-top-color:#fff; border-radius:50%; animation:txnSpin .65s linear infinite; }
        @keyframes txnSpin { to{transform:rotate(360deg)} }

        .txn-table-wrap { overflow-x:auto; width:100%; }
        .txn-table-wrap::-webkit-scrollbar { height:4px; }
        .txn-table-wrap::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }
        .txn-table { width:100%; border-collapse:separate; border-spacing:0; table-layout:auto; }

        .txn-thead-th { padding:11px 12px 9px; background:#f8fafc; border-bottom:1.5px solid #e2e8f0; text-align:left; white-space:nowrap; user-select:none; }
        .txn-thead-th--sort { cursor:pointer; }
        .txn-thead-th--sort:hover { background:#f1f5f9; }
        .txn-th-inner { display:flex; align-items:center; gap:5px; }
        .txn-th { font-size:11px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.5px; }
        .txn-sort-icon { color:#94a3b8; display:flex; align-items:center; }

        .txn-tbody-tr { transition:background .12s; }
        .txn-tbody-tr:hover { background:#f8fafc; }
        .txn-tbody-tr--expanded { background:#f8fbff; }
        .txn-tbody-td { padding:11px 12px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }
        .txn-tbody-td:first-child { width:42px; padding-left:16px; padding-right:4px; }
        .txn-expand-btn {
          margin-top: -3px;width:26px; height:26px; border-radius:7px; border:1px solid #dbe3ef;
          background:#fff; color:#475569; display:flex; align-items:center;
          justify-content:center; font-size:17px; line-height:1; font-weight:600;
          cursor:pointer; transition:all .15s; padding:0;
        }
        .txn-expand-btn:hover, .txn-expand-btn.is-expanded {
          background:#eff6ff; border-color:#93c5fd; color:#2563eb;
        }

        .txn-details-tr { background:#f8fbff; }
        .txn-details-td { padding:14px 14px 14px 14px; border-bottom:1px solid #e2e8f0; }
        .txn-details-panel {
          border:1px solid #dbe7f5; border-radius:12px; background:#fff;
          box-shadow:0 2px 8px rgba(15,23,42,.04); overflow:hidden;
        }
        .txn-details-header {
          display:flex; align-items:center; justify-content:space-between; gap:20px;
          padding:14px 16px; border-bottom:1px solid #edf2f7;
          background:linear-gradient(180deg,#f8fbff,#fff);
        }
        .txn-details-title { font-size:13px; font-weight:800; color:#0f172a; }
        .txn-details-subtitle { margin-top:2px; font-size:11px; color:#94a3b8; font-weight:500; }
        .txn-details-date { text-align:right; display:flex; flex-direction:column; gap:2px; }
        .txn-details-date span { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:.4px; }
        .txn-details-date strong { font-size:11.5px; color:#334155; font-weight:700; }
        .txn-details-grid {
          display:grid; grid-template-columns:repeat(4,minmax(0,1fr));
          gap:0;
        }
        .txn-detail-item {
          min-width:0; padding:12px 15px; border-right:1px solid #edf2f7;
          border-bottom:1px solid #edf2f7; display:flex; flex-direction:column; gap:5px;
        }
        .txn-detail-item:nth-child(4n) { border-right:none; }
        .txn-detail-label {
          font-size:10px; color:#94a3b8; font-weight:700; text-transform:uppercase;
          letter-spacing:.45px;
        }
        .txn-detail-value {
          min-width:0; font-size:12px; color:#334155; font-weight:600;
          overflow:hidden; text-overflow:ellipsis;
        }
        .txn-detail-copy { display:block; }
        .txn-card-detail, .txn-gateway-detail { display:flex; align-items:center; gap:7px; }
        .txn-card-detail img { max-width:48px; height:auto; }
        .txn-gateway-detail img { width:17px; height:17px; object-fit:contain; }
        .txn-detail-history { padding:14px 15px 16px; }
        .txn-detail-section-title {
          font-size:11px; font-weight:800; color:#334155; margin-bottom:8px;
          text-transform:uppercase; letter-spacing:.45px;
        }
        .txn-detail-history-box {
          border:1px solid #edf2f7; border-radius:9px; padding:0 12px;
          background:#fbfdff; max-height:220px; overflow:auto;
        }

        /* Cell styles */
        .txn-client-cell { display:flex; align-items:center; gap:7px; }
        .txn-client-avatar { width:26px; height:26px; border-radius:7px; flex-shrink:0; background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:800; color:#2563eb; }
        .txn-client-name { font-size:12.5px; font-weight:600; color:#0f172a; white-space:nowrap; }
        .txn-date { font-size:12px; font-weight:600; color:#0f172a; white-space:nowrap; }
        .txn-amount-cell { display:flex; flex-direction:column; gap:2px; }
        .txn-amount { font-size:13px; font-weight:700; color:#0f172a; white-space:nowrap; }
        .txn-amount-conv { font-size:11px; color:#94a3b8; font-weight:500; }
        .txn-gateway-cell { display:flex; align-items:center; gap:6px; }
        .txn-gateway-name { font-size:12.5px; font-weight:600; color:#0f172a; }
        .txn-descriptor { font-size:12.5px; font-weight:500; color:#475569; }
        .txn-muted { font-size:12.5px; color:#cbd5e1; font-weight:500; }
        .txn-status-trigger { background:none; border:none; cursor:pointer; display:flex; align-items:center; }
        .txn-actions { display:flex; align-items:center; gap:5px; }
        .txn-action-btn { width:28px; height:28px; border-radius:7px; background:#f8fafc; border:1.5px solid #e2e8f0; color:#64748b; display:flex; align-items:center; justify-content:center; transition:all .15s; text-decoration:none; cursor:pointer; }
        .txn-action-btn:hover { background:#eff6ff; border-color:#bfdbfe; color:#2563eb; }

        /* History tooltip */
        .txn-history-item { padding:8px 0; border-bottom:1px solid #f1f5f9; }
        .txn-history-item:last-child { border-bottom:none; }
        .txn-history-txt { font-size:12px; color:#374151; line-height:1.5; }
        .txn-no-history { font-size:12px; color:#94a3b8; font-style:italic; }

        /* Loading / empty */
        .txn-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px; gap:12px; }
        .txn-spinner { width:28px; height:28px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:txnSpin .7s linear infinite; }
        .txn-loading-txt { font-size:13px; color:#94a3b8; font-weight:500; }
        .txn-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px 24px; gap:10px; }
        .txn-empty-icon { width:52px; height:52px; border-radius:13px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#cbd5e1; }
        .txn-empty-title { font-size:14px; font-weight:700; color:#64748b; }
        .txn-empty-sub { font-size:12.5px; color:#94a3b8; font-weight:500; text-align:center; }

        .txn-pagination-wrap { padding:12px 16px; border-top:1px solid #f1f5f9; background:#fafbfc; }

        @media (max-width:1100px) {
          .txn-details-grid { grid-template-columns:repeat(3,minmax(0,1fr)); }
          .txn-detail-item:nth-child(4n) { border-right:1px solid #edf2f7; }
          .txn-detail-item:nth-child(3n) { border-right:none; }
        }
        @media (max-width:850px) {
          .txn-details-grid { grid-template-columns:repeat(2,minmax(0,1fr)); }
          .txn-detail-item:nth-child(3n) { border-right:1px solid #edf2f7; }
          .txn-detail-item:nth-child(2n) { border-right:none; }
          .txn-details-td { padding-left:30px; }
          .txn-th { font-size:10px; }
          .txn-tbody-td { padding-left:8px; padding-right:8px; }
        }
        @media (max-width:700px) {
          .txn-header { flex-direction:column; align-items:flex-start; }
          .txn-header-right { width:100%; }
          .txn-table-wrap { overflow-x:auto; }
          .txn-table { min-width:850px; }
          .txn-details-td { padding-left:14px; }
        }
      `}</style>
    </div>
  );
}

export default PaymentTable;